import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { Icon } from '@components/icons';

const StyledJobsSection = styled.section`
  .mobile-heading {
    margin-bottom: 16px;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--lightest-slate);

    @media (min-width: 1024px) {
      display: none;
    }
  }

  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .list:hover .item {
    opacity: 0.5;
  }

  .list .item:hover,
  .list .item:focus-within {
    opacity: 1;
  }

  .resume {
    display: inline-flex;
    align-items: center;
    margin-top: 12px;
    font-weight: 500;
    color: var(--lightest-slate);

    svg {
      width: 16px;
      height: 16px;
      margin-left: 4px;
      transition: transform 0.15s ease;
    }

    &:hover {
      color: var(--green);

      svg {
        transform: translate(2px, -2px);
      }
    }
  }
`;

const JobCard = styled.li`
  position: relative;
  margin-bottom: 12px;
  padding-bottom: 4px;
  transition: opacity 0.15s ease;

  .glow {
    display: none;
    position: absolute;
    z-index: 0;
    border-radius: 8px;
    inset: -16px -24px;
    transition: background 0.15s ease;

    @media (min-width: 1024px) {
      display: block;
    }
  }

  &:hover .glow,
  &:focus-within .glow {
    background: rgba(30, 41, 59, 0.5);
    box-shadow: inset 0 1px 0 0 rgba(148, 163, 184, 0.1);
  }

  a.stretch {
    position: absolute;
    inset: 0;
    z-index: 1;

    span {
      ${({ theme }) => theme.mixins.visuallyHidden};
    }
  }

  .card {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;

    @media (min-width: 640px) {
      grid-template-columns: 1fr 3fr;
      gap: 16px;
    }
  }

  .range {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--dark-slate);
    padding-top: 4px;
  }

  h3 {
    font-size: 16px;
    font-weight: 500;
    color: var(--lightest-slate);
    margin: 0;
    line-height: 1.4;

    svg {
      display: inline-block;
      width: 16px;
      height: 16px;
      margin-left: 4px;
      vertical-align: -2px;
      transition: transform 0.15s ease;
    }
  }

  &:hover h3,
  &:focus-within h3 {
    color: var(--green);

    svg {
      transform: translate(2px, -2px);
    }
  }

  .body {
    position: relative;
    z-index: 2;
    margin-top: 8px;
    font-size: 14px;
    color: var(--slate);

    ul {
      margin: 0;
      padding: 0;
      list-style: none;
    }

    li {
      margin-bottom: 6px;
    }
  }

  .tech {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 12px;
    padding: 0;
    list-style: none;
  }

  .tech li {
    border-radius: 999px;
    background: var(--green-tint);
    color: var(--green);
    font-size: 12px;
    font-weight: 500;
    padding: 4px 12px;
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              range
              url
              tech
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  return (
    <StyledJobsSection id="jobs" tabIndex="-1">
      <h2 className="mobile-heading">Experience</h2>
      <ol className="list">
        {jobsData.map(({ node }, i) => {
          const { frontmatter, html } = node;
          const { title, url, company, range, tech } = frontmatter;

          return (
            <JobCard className="item" key={i}>
              <div className="glow" />
              <div className="card">
                <a className="stretch" href={url}>
                  <span>
                    {title} at {company}
                  </span>
                </a>
                <div className="range">{range}</div>
                <div>
                  <h3>
                    {title} · {company}
                    <Icon name="External" />
                  </h3>
                  <div className="body" dangerouslySetInnerHTML={{ __html: html }} />
                  {tech && (
                    <ul className="tech">
                      {tech.map(item => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </JobCard>
          );
        })}
      </ol>
      <a
        className="resume"
        href="https://www.linkedin.com/in/satenderrathore"
        target="_blank"
        rel="noreferrer">
        View Full Résumé
        <Icon name="External" />
      </a>
    </StyledJobsSection>
  );
};

export default Jobs;
